// <TierBAtmosphere> — the world's atmosphere over a standard template render.
//
// THE PROBLEM IT SOLVES. A "theme world" only ever existed on the handful of
// components with a bespoke Tier-A renderer: two per world, out of ~17 slide
// types. Everywhere else the world collapsed to palette + fonts on a flat
// background — pick Neon Oracle and a drag-sort slide in the same lesson showed
// no grid, no scanlines, nothing. THEME_WORLDS_PLAN.md §9 calls this the
// highest-leverage unstarted work; this is it.
//
// Tier B = the standard component render + the world's atmosphere behind it +
// the world's palette/fonts (already applied via the theme tokens). Built once
// at the dispatch boundary, so every non-bespoke template gets it for free.
//
// WHY THE BACKGROUND OVERRIDE. Standard templates set an opaque
// `background: theme.colors.background` on their own root, inline — which would
// paint straight over the atmosphere. The scoped rule below clears it on the
// template root only (marked by `.tw-tierb-slot`), so that root turns
// transparent while every card, panel and surface inside it keeps its own fill.
// Inline styles need `!important` to beat, hence its use here; it is
// deliberately confined to one property on one element.
//
// WHY THE WRAP GOES OUTSIDE THE SCROLL BOX. A long text slide scrolls rather
// than being cut off. This atmosphere is `overflow: hidden` and full-height, so
// wrapping BETWEEN the scroller and its content puts a `height: 100%` clipping
// box in the middle: the overflow is swallowed, the scroller sees nothing to
// scroll, and the slide silently goes back to being cut. It therefore wraps the
// whole scroll box and never comes between it and its content.
// (It previously had to sit outside for a related reason — a JS fitting pass
// measured those two elements against each other — but that pass never worked
// and has been removed. The constraint survives it.)

import { useEffect } from 'react';
import type { ReactNode } from 'react';
import type { Theme, ThemeId } from '../../components/course-builder/slides/slideTypes';
import { ThemeWorldAtmosphere } from './Atmosphere';

const STYLE_ID = 'tw-tierb-styles';

/** Themes that are full worlds (palette + atmosphere + audio), not plain skins. */
const WORLD_IDS: ReadonlySet<ThemeId> = new Set<ThemeId>([
  'neon-oracle',
  'obsidian',
  'aurora',
  'terra',
  'hyperpop',
  'risograph',
]);

/**
 * Per-world templates that already render their own <WorldStage> (Tier A) or a
 * bespoke composed skin. Double-wrapping those would stack two atmospheres and
 * two backgrounds, so they opt out here.
 *
 * Keep in step with THEME_WORLDS_PLAN.md §6 when a new Tier-A pairing lands.
 */
const BESPOKE: Readonly<Partial<Record<ThemeId, ReadonlySet<string>>>> = {
  'neon-oracle': new Set(['scenario-simulation', 'spot-the-error']),
  obsidian: new Set(['claim-evidence', 'tension-mapper']),
  aurora: new Set(['confidence-meter', 'before-after-bridge']),
  terra: new Set(['progressive-builder', 'timeline-navigator']),
  hyperpop: new Set(['reveal-predict', 'drag-sort']),
  risograph: new Set(['perspective-lenses', 'gallery-walk']),
};

/** Every world has a bespoke composed-experience renderer. */
const ALWAYS_BESPOKE: ReadonlySet<string> = new Set(['composed-experience']);

export function isThemeWorld(themeId: ThemeId): boolean {
  return WORLD_IDS.has(themeId);
}

export function hasBespokeRenderer(themeId: ThemeId, template: string): boolean {
  if (ALWAYS_BESPOKE.has(template)) return true;
  return BESPOKE[themeId]?.has(template) ?? false;
}

/** True when this (theme, template) pair should get the Tier-B treatment. */
export function shouldWrapInAtmosphere(themeId: ThemeId, template: string): boolean {
  return isThemeWorld(themeId) && !hasBespokeRenderer(themeId, template);
}

function useTierBStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = `
.tw-tierb { position: relative; height: 100%; width: 100%; }
.tw-tierb > .tw-tierb-content { position: relative; z-index: 1; height: 100%; width: 100%; }
/* Clear ONLY the template root's own opaque fill so the atmosphere reads
   through. Inner surfaces (cards, panels, wells) are deeper and keep theirs.
   .tw-tierb-slot is the content wrapper SlideCanvas tags when it wraps. */
.tw-tierb .tw-tierb-slot > * { background-color: transparent !important; }
`;
      document.head.appendChild(tag);
    }
    tag.dataset.refcount = String(Number(tag.dataset.refcount || '0') + 1);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
  }, []);
}

export function TierBAtmosphere({
  theme,
  children,
}: {
  theme: Theme;
  children: ReactNode;
}) {
  useTierBStyles();

  return (
    <div className="tw-tierb" style={{ background: theme.colors.background }}>
      <ThemeWorldAtmosphere
        worldId={theme.id}
        primary={theme.colors.primary}
        accent={theme.colors.accent || theme.colors.primary}
        background={theme.colors.background}
        style={{ zIndex: 0 }}
      />
      <div className="tw-tierb-content">{children}</div>
    </div>
  );
}
